/* ═══════════════════════════════════════════════════════
   Honza PDP — <honza-product> custom element
   Variant selection (swatches / list / pills), price updates,
   gallery (hero + thumbnails or stacked), lightbox, quantity,
   accordion icons. Section-scoped; safe to reload in the editor.
   ═══════════════════════════════════════════════════════ */
(function () {
  if (customElements.get('honza-product')) return;

  var FALLBACK_STRINGS = {
    addToCart: 'Add to cart',
    soldOut: 'Sold out',
    unavailable: 'Unavailable',
  };

  function strings() {
    return Object.assign({}, FALLBACK_STRINGS, window.variantStrings || {});
  }

  class HonzaProduct extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('[data-honza-variants]');
      try {
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.addBtn = this.querySelector('[data-add-to-cart]');
      this.addBtnText = this.addBtn ? this.addBtn.querySelector('span') : null;
      this.priceEl = this.querySelector('[data-price]');
      this.compareEl = this.querySelector('[data-compare-price]');
      this.soldBadge = this.querySelector('[data-sold-out-badge]');
      this.gallery = this.querySelector('[data-gallery]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.lightboxImage = this.lightbox ? this.lightbox.querySelector('[data-lightbox-image]') : null;

      this.bindOptions();
      this.bindGallery();
      this.bindLightbox();
      this.bindQuantity();
      this.bindAccordions();

      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('keydown', this.onKeydown);

      var current = this.currentVariant();
      if (current) {
        this.refreshOptionAvailability(current);
        this.refreshListPrices();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('honza-lightbox-open');
    }

    /* ── Options ─────────────────────────────────────── */
    bindOptions() {
      var self = this;
      this.querySelectorAll('[data-option-value]').forEach(function (control) {
        control.addEventListener('click', function () {
          var group = control.closest('[data-option-group]');
          if (!group) return;
          group.querySelectorAll('[data-option-value]').forEach(function (c) {
            c.classList.remove('is-selected');
            c.setAttribute('aria-checked', 'false');
          });
          control.classList.add('is-selected');
          control.setAttribute('aria-checked', 'true');

          var valueLabel = group.querySelector('[data-selected-value]');
          if (valueLabel) valueLabel.textContent = control.getAttribute('data-option-value');

          self.updateVariant();
        });
      });
    }

    selectedOptions() {
      var selected = [];
      this.querySelectorAll('[data-option-group]').forEach(function (group) {
        var position = parseInt(group.getAttribute('data-option-position'), 10);
        var active = group.querySelector('[data-option-value].is-selected');
        if (!isNaN(position) && active) selected[position - 1] = active.getAttribute('data-option-value');
      });
      return selected;
    }

    findVariant(selected) {
      return this.variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return selected[i] === undefined || opt === selected[i];
        });
      });
    }

    currentVariant() {
      var id = this.variantInput ? parseInt(this.variantInput.value, 10) : NaN;
      var byId = this.variants.find(function (v) { return v.id === id; });
      return byId || this.findVariant(this.selectedOptions()) || null;
    }

    updateVariant() {
      var selected = this.selectedOptions();
      var match = this.findVariant(selected);

      if (!match) {
        this.setUnavailable();
        this.refreshOptionAvailability(null, selected);
        this.refreshListPrices(selected);
        return;
      }

      if (this.variantInput) this.variantInput.value = match.id;

      this.renderPrice(match);
      this.renderAvailability(match);
      this.refreshOptionAvailability(match);
      this.refreshListPrices(selected);
      this.showImage(match.image_position, true);

      var url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }

    renderPrice(variant) {
      if (this.priceEl) this.priceEl.textContent = variant.price_formatted;

      if (this.compareEl) {
        if (variant.compare_formatted) {
          this.compareEl.textContent = variant.compare_formatted;
          this.compareEl.removeAttribute('hidden');
        } else {
          this.compareEl.setAttribute('hidden', '');
        }
      }
    }

    renderAvailability(variant) {
      var s = strings();
      var available = !!variant.available;

      if (this.variantInput) this.variantInput.disabled = !available;
      if (this.addBtn) {
        this.addBtn.disabled = !available;
        this.addBtn.removeAttribute('aria-disabled');
      }
      if (this.addBtnText) this.addBtnText.textContent = available ? s.addToCart : s.soldOut;
      if (this.soldBadge) this.soldBadge.toggleAttribute('hidden', available);
    }

    setUnavailable() {
      var s = strings();
      if (this.variantInput) this.variantInput.disabled = true;
      if (this.addBtn) this.addBtn.disabled = true;
      if (this.addBtnText) this.addBtnText.textContent = s.unavailable;
      if (this.soldBadge) this.soldBadge.setAttribute('hidden', '');
    }

    /* Marks each option value as unavailable when no purchasable variant
       exists for it, given the other currently selected options. */
    refreshOptionAvailability(variant, selectedOverride) {
      var selected = selectedOverride || (variant ? variant.options.slice() : this.selectedOptions());
      var variants = this.variants;

      this.querySelectorAll('[data-option-group]').forEach(function (group) {
        var position = parseInt(group.getAttribute('data-option-position'), 10) - 1;
        group.querySelectorAll('[data-option-value]').forEach(function (control) {
          var value = control.getAttribute('data-option-value');
          var hasAvailable = variants.some(function (v) {
            if (!v.available || v.options[position] !== value) return false;
            return v.options.every(function (opt, i) {
              return i === position || selected[i] === undefined || opt === selected[i];
            });
          });
          control.classList.toggle('is-unavailable', !hasAvailable);
        });
      });
    }

    /* Updates the price shown next to each list row (e.g. Size) using the
       variant that matches that row plus the other selected options. */
    refreshListPrices(selectedOverride) {
      var selected = selectedOverride || this.selectedOptions();
      var variants = this.variants;

      this.querySelectorAll('[data-option-group][data-show-prices]').forEach(function (group) {
        var position = parseInt(group.getAttribute('data-option-position'), 10) - 1;
        group.querySelectorAll('[data-option-value]').forEach(function (row) {
          var priceEl = row.querySelector('[data-row-price]');
          if (!priceEl) return;
          var value = row.getAttribute('data-option-value');

          var exact = variants.find(function (v) {
            return v.options[position] === value && v.options.every(function (opt, i) {
              return i === position || selected[i] === undefined || opt === selected[i];
            });
          });
          var fallback = exact || variants.find(function (v) { return v.options[position] === value; });
          if (fallback) priceEl.textContent = fallback.price_formatted;
        });
      });
    }

    /* ── Gallery ─────────────────────────────────────── */
    bindGallery() {
      var self = this;
      if (!this.gallery) return;

      this.gallery.querySelectorAll('[data-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          self.showImage(thumb.getAttribute('data-image-position'), false);
        });
      });

      this.gallery.querySelectorAll('[data-zoom]').forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var active = self.gallery.querySelector('[data-hero-image].is-active') || trigger;
          var src = active.getAttribute('data-full-image') || trigger.getAttribute('data-full-image');
          var alt = active.getAttribute('data-alt') || trigger.getAttribute('data-alt');
          self.openLightbox(src, alt);
        });
      });

      var reveals = this.gallery.querySelectorAll('.reveal');
      if (reveals.length && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              e.target.classList.add('visible');
              observer.unobserve(e.target);
            }
          });
        }, { threshold: 0.08 });
        reveals.forEach(function (el) { observer.observe(el); });
      } else {
        reveals.forEach(function (el) { el.classList.add('visible'); });
      }
    }

    showImage(position, scroll) {
      if (!this.gallery || !position || String(position) === '0') return;
      var pos = String(position);
      var layout = this.gallery.getAttribute('data-gallery-layout');

      this.gallery.querySelectorAll('[data-thumb]').forEach(function (thumb) {
        var active = thumb.getAttribute('data-image-position') === pos;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      var target = null;
      this.gallery.querySelectorAll('[data-hero-image]').forEach(function (img) {
        var active = img.getAttribute('data-image-position') === pos;
        img.classList.toggle('is-active', active);
        if (active) target = img;
      });

      if (layout === 'stacked' && scroll && target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /* ── Lightbox ────────────────────────────────────── */
    bindLightbox() {
      var self = this;

      this.querySelectorAll('[data-measurement-zoom]').forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          self.openLightbox(trigger.getAttribute('data-full-image'), trigger.getAttribute('data-alt'));
        });
      });

      if (!this.lightbox) return;
      var close = this.lightbox.querySelector('[data-lightbox-close]');
      if (close) close.addEventListener('click', function () { self.closeLightbox(); });
      this.lightbox.addEventListener('click', function (event) {
        if (event.target === self.lightbox) self.closeLightbox();
      });
    }

    openLightbox(src, alt) {
      if (!this.lightbox || !this.lightboxImage || !src) return;
      this.lightboxImage.src = src;
      this.lightboxImage.alt = alt || '';
      this.lightbox.removeAttribute('hidden');
      document.body.classList.add('honza-lightbox-open');
    }

    closeLightbox() {
      if (!this.lightbox || !this.lightboxImage) return;
      this.lightbox.setAttribute('hidden', '');
      document.body.classList.remove('honza-lightbox-open');
      this.lightboxImage.src = '';
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.lightbox && !this.lightbox.hasAttribute('hidden')) {
        this.closeLightbox();
      }
    }

    /* ── Quantity ────────────────────────────────────── */
    bindQuantity() {
      var input = this.querySelector('[data-quantity-input]');
      if (!input) return;

      this.querySelectorAll('[data-quantity-change]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var delta = parseInt(btn.getAttribute('data-quantity-change'), 10) || 0;
          var min = parseInt(input.getAttribute('min'), 10) || 1;
          var next = (parseInt(input.value, 10) || min) + delta;
          input.value = Math.max(min, next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    /* ── Accordions ──────────────────────────────────── */
    bindAccordions() {
      this.querySelectorAll('.honza-pdp-accordion').forEach(function (acc) {
        var icon = acc.querySelector('.honza-pdp-accordion-icon');
        if (!icon) return;
        icon.textContent = acc.open ? '−' : '+';
        acc.addEventListener('toggle', function () {
          icon.textContent = acc.open ? '−' : '+';
        });
      });
    }
  }

  customElements.define('honza-product', HonzaProduct);
})();
